import { ReactNode } from "react";

import { RadioGroup, Text } from "@vericus/cadmus-ui";

import { SocraticDialogueFragment } from "@/generated/graphql";

import {
  CompletedDialogue,
  toCompletedDialogues,
} from "../../helpers/to-completed-dialogues";
import { SubmittedVideo } from "../submitted-video";
import * as styles from "./attempts-list.css";

type Props = {
  /** All dialogues for the session. */
  dialogues: SocraticDialogueFragment[];
  /** ID of the dialogue currently written to the answer snapshot. */
  selectedDialogueId: string | null;
  /** Called when the student picks a different attempt to submit. */
  onSelectAttempt: (dialogue: SocraticDialogueFragment) => void;
  /** Whether the student still has re-attempts available. */
  hasReattemptsRemaining?: boolean;
  /** Re-attempt controls rendered in a column to the right of the attempts. */
  headerAction?: ReactNode;
  isReadonly?: boolean;
};

export function AttemptsList(props: Props) {
  const {
    dialogues,
    selectedDialogueId,
    onSelectAttempt,
    headerAction,
    isReadonly = false,
  } = props;

  const completedDialogues: CompletedDialogue[] =
    toCompletedDialogues(dialogues);

  const completedAttemptsCount = completedDialogues.length;
  const isOnlyAttempt = completedAttemptsCount === 1;
  const singleDialogue = completedDialogues[0];

  const headingLabel = isOnlyAttempt
    ? "You've submitted a response"
    : "Please select a response to submit";

  return (
    <div className={styles.root}>
      <div className={styles.main}>
        <Text kind="headingSix">{headingLabel}</Text>

        {isOnlyAttempt && singleDialogue != null && (
          <div className={styles.singleCard}>
            <SubmittedVideo
              headingLabel={`Attempt ${singleDialogue.attemptNumber}`}
              mediaUrl={singleDialogue.mediaUrl}
              durationSeconds={singleDialogue.durationSeconds}
              isSelected={singleDialogue.id === selectedDialogueId}
            />
          </div>
        )}

        {!isOnlyAttempt && completedAttemptsCount > 0 && (
          <RadioGroup.Root
            disabled={isReadonly}
            className={styles.list}
            value={selectedDialogueId ?? undefined}
            onValueChange={(value) => {
              const dialogue = dialogues.find((d) => d.id === value);
              if (dialogue) {
                onSelectAttempt(dialogue);
              }
            }}
          >
            {completedDialogues.map((dialogue) => {
              const attemptLabel = `Attempt ${dialogue.attemptNumber}`;
              return (
                <RadioGroup.Option key={dialogue.id} value={dialogue.id}>
                  <SubmittedVideo
                    headingLabel={attemptLabel}
                    mediaUrl={dialogue.mediaUrl}
                    durationSeconds={dialogue.durationSeconds}
                    isSelected={dialogue.id === selectedDialogueId}
                  />
                </RadioGroup.Option>
              );
            })}
          </RadioGroup.Root>
        )}
      </div>

      {headerAction != null && (
        <div className={styles.actionSlot}>{headerAction}</div>
      )}
    </div>
  );
}
